// src/vdr/requestlist.ts
//
// Request-list VDR (shown as "Kourtney" until we know the provider's name).
// The index has one row per request: Category, Item #, Item Name, ..., Documents
// ("a.pdf; b.xlsx"). The VDR's ZIP is laid out Category/"<Item #> - <Item Name>"/file,
// and one document can be filed under several requests. Files carry no number
// codes, so uploads are routed by file name through the Documents column.
//
// Codes are internal only: a category is its position in the index ("1".."N"), a
// request is "<category>.<Item #>". Folders are named by meta.folderName, which
// matches the VDR's ZIP exactly, so Folder Mirroring and this provider share one tree.
import * as XLSX from "xlsx";
import type { IndexRow } from "./providers";

/** The cleanup the VDR applies to its ZIP folder names (checked against a real export). */
function vdrFolderName(name: string, maxLength?: number): string {
  let s = name.replace(/[\\/:*?"<>|]/g, "-").replace(/[ .]+$/, "");
  if (maxLength && s.length > maxLength) s = s.substring(0, maxLength).replace(/[ .]+$/, "");
  return s;
}

export async function parseIndexFile(file: File): Promise<IndexRow[]> {
  console.log(`🧾 [requestlist] parseIndexFile: name="${file.name}" size=${file.size}`);

  // SheetJS handles the line breaks inside quoted Description cells
  const wb = /\.xlsx?$/i.test(file.name)
    ? XLSX.read(await file.arrayBuffer(), { type: "array" })
    : XLSX.read((await file.text()).replace(/^﻿/, ""), { type: "string" }); // BOM would rename "Category"
  const records = XLSX.utils.sheet_to_json<Record<string, unknown>>(wb.Sheets[wb.SheetNames[0]], {
    defval: "",
    raw: false,
  });

  const first = records[0] ?? {};
  if (!("Category" in first) || !("Item #" in first) || !("Item Name" in first)) {
    throw new Error(
      'This index is missing the "Category", "Item #" or "Item Name" column. Contact your File Pigeon admin.'
    );
  }

  const rows: IndexRow[] = [];
  const categoryCodes = new Map<string, string>(); // every category, so codes stay stable
  const categoriesWithFolders = new Set<string>();

  for (const rec of records) {
    const category = String(rec["Category"] ?? "");
    const itemNum = String(rec["Item #"] ?? "").trim();
    const itemName = String(rec["Item Name"] ?? "");
    if (!category.trim() && !itemNum) continue; // blank line

    if (!/^\d+$/.test(itemNum)) {
      throw new Error(
        `Couldn't read the Item # "${itemNum}" for "${itemName.slice(0, 60)}". Contact your File Pigeon admin.`
      );
    }

    let catCode = categoryCodes.get(category);
    if (!catCode) {
      catCode = String(categoryCodes.size + 1);
      categoryCodes.set(category, catCode);
    }

    const documents = String(rec["Documents"] ?? "")
      .split("; ")
      .map((d) => d.trim())
      .filter(Boolean);

    // Only requests with documents get a folder — the same set the VDR's ZIP (and so
    // Folder Mirroring) has. An empty request gets one once a newer index lists a document.
    if (documents.length === 0) continue;

    if (!categoriesWithFolders.has(catCode)) {
      categoriesWithFolders.add(catCode);
      rows.push({ code: catCode, title: category, meta: { folderName: vdrFolderName(category) } });
    }

    rows.push({
      code: `${catCode}.${itemNum}`,
      title: itemName,
      meta: { folderName: vdrFolderName(`${itemNum} - ${vdrFolderName(itemName)}`, 200), documents },
    });
  }

  if (rows.length === 0) {
    throw new Error("No request in this index has documents yet, so there are no folders to create.");
  }
  console.log(`   ↳ ${categoriesWithFolders.size} categories, ${rows.length - categoriesWithFolders.size} requests with documents`);
  return rows;
}

/** Every request code whose Documents list includes this file name. */
export function requestListCodesForFile(fileName: string, rows: IndexRow[]): string[] {
  const name = fileName.toLowerCase();
  return rows
    .filter((r) => (r.meta?.documents as string[] | undefined)?.some((d) => d.toLowerCase() === name))
    .map((r) => r.code);
}

/** "Category folder/Request folder" for a code, as shown in the preview and email. */
export function requestListFolderPath(code: string, rows: IndexRow[]): string {
  const name = (c: string) => (rows.find((r) => r.code === c)?.meta?.folderName as string | undefined) ?? c;
  const cat = code.split(".")[0];
  return code.includes(".") ? `${name(cat)}/${name(code)}` : name(cat);
}
